/**
 * Creates a debounced function with leading and trailing options
 *
 * @param func - The function to debounce
 * @param wait - The time in milliseconds to delay execution
 * @param options - Configuration object with leading/trailing flags
 * @returns A debounced version of the provided function
 *
 * Behavior options:
 * - leading=true: Execute on the leading edge (first invocation)
 * - trailing=true: Execute on the trailing edge (after wait time)
 * - Both flags can be true for execution on both edges
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = { leading: false, trailing: true },
): (...args: Parameters<T>) => void {
  // Default options
  const leading = options.leading !== undefined ? options.leading : false
  const trailing = options.trailing !== undefined ? options.trailing : true

  // Track timer, most recent arguments, and execution state
  let timeout: ReturnType<typeof setTimeout> | null = null
  let lastArgs: Parameters<T> | null = null
  let wasLeadingCalled = false

  // Reset state function
  const reset = () => {
    timeout = null
    lastArgs = null
    wasLeadingCalled = false
  }

  // Return the debounced wrapper function
  return (...args: Parameters<T>) => {
    // Always capture the most recent arguments
    lastArgs = args

    // Handle the leading edge execution
    if (!timeout && leading && !wasLeadingCalled) {
      // Execute the function immediately
      func(...args)
      wasLeadingCalled = true
    }

    // Clear previous timeout if exists
    if (timeout !== null) {
      clearTimeout(timeout)
    }

    // Set timeout for trailing edge execution
    timeout = setTimeout(() => {
      // If trailing edge option is true and we have arguments
      // (or if leading wasn't already called), execute function
      if (trailing && lastArgs) {
        func(...lastArgs)
      }

      // Reset all state for next debounce cycle
      reset()
    }, wait)
  }
}
